import { Star, MapPin, Heart, ShieldCheck, Briefcase, Navigation } from "lucide-react";
import { rupiah } from "../../lib/format";
import { useLang } from "../../context/LanguageContext";
import { Button } from "../ui/button";

const BADGE_STYLES = {
  top_rated: "bg-orange-400 text-white",
  pilihan_utama: "bg-blue-600 text-white",
  tersedia: "bg-emerald-500 text-white",
};

export default function PublicInspectorCard({ insp, onLogin, list = false }) {
  const { t } = useLang();
  const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(insp.name)}&background=2563eb&color=fff`;

  const badge = insp.badge && (
    <span className={`absolute top-3 left-3 z-10 text-[10px] font-extrabold rounded-md px-2.5 py-1 shadow ${BADGE_STYLES[insp.badge]}`} data-testid={`badge-${insp.user_id}`}>
      {t(`inspectors.badges.${insp.badge}`)}
    </span>
  );
  const heart = (
    <button onClick={onLogin} data-testid={`fav-btn-${insp.user_id}`} aria-label="favorite"
      className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-red-50 transition-colors">
      <Heart size={16} className="text-slate-400 hover:text-red-400" />
    </button>
  );
  const img = (
    <img src={insp.picture || fallback} alt={insp.name} loading="lazy"
      onError={(e) => { e.currentTarget.src = fallback; }}
      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
  );

  const meta = (
    <>
      <div className="flex items-center gap-1.5">
        <p className="text-[15px] font-extrabold text-slate-900 truncate">{insp.name}</p>
        <ShieldCheck size={15} className="text-blue-600 shrink-0" fill="#dbeafe" />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-amber-500 font-bold flex items-center gap-1">
          <Star size={12} fill="#f59e0b" /> {insp.rating_avg || "Baru"}
          <span className="text-slate-400 font-medium">({t("inspectors.reviews", { n: insp.rating_count })})</span>
        </p>
        <p className="text-[11px] text-slate-400 font-semibold flex items-center gap-0.5"><Navigation size={11} /> {insp.distance_km} km</p>
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1.5"><MapPin size={12} className="text-slate-400" /> {insp.city}</p>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Briefcase size={12} className="text-slate-400" /> {t("inspectors.exp", { n: insp.experience_years })}</p>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {insp.vehicle_types.map((v) => (
          <span key={v} className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-blue-50 text-blue-600">{t(`vehicle.${v}`)}</span>
        ))}
      </div>
    </>
  );

  const priceBlock = (
    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
      <div>
        <p className="text-base font-extrabold text-slate-900">{rupiah(insp.min_price)}</p>
        <p className="text-[11px] text-slate-400 font-medium">{t("inspectors.mulaiDari")}</p>
      </div>
      <Button onClick={onLogin} variant="outline" data-testid={`view-profile-${insp.user_id}`}
        className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 font-bold rounded-lg h-9 px-4 text-xs">
        {t("inspectors.lihatProfil")}
      </Button>
    </div>
  );

  if (list) {
    return (
      <div data-testid={`inspector-card-${insp.user_id}`} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all overflow-hidden flex">
        <div className="relative w-44 shrink-0 overflow-hidden bg-slate-100">{badge}{img}</div>
        <div className="flex-1 p-4 relative">{heart}{meta}{priceBlock}</div>
      </div>
    );
  }
  return (
    <div data-testid={`inspector-card-${insp.user_id}`} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-200 transition-all overflow-hidden flex flex-col">
      <div className="relative h-40 overflow-hidden bg-slate-100">{badge}{heart}{img}</div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex-1">{meta}</div>
        {priceBlock}
      </div>
    </div>
  );
}
